// The filters a visitor sets, between the address bar and the filter panel
// (listing browse plan, step 3). The panel edits a copy; "Show 214 listings"
// writes it back to the address, which is the one place a search lives - it
// can be shared, bookmarked and undone with Back.

import { CONDO_TYPES, HOUSE_TYPES } from '@/lib/property-types';

export type FilterValues = {
  minPrice: string;
  maxPrice: string;
  bedrooms: string;
  bathrooms: string;
  propertyType: string[];
  ownerType: '' | 'private' | 'agency';
  // Under "More filters".
  minArea: string;
  maxArea: string;
  minYear: string;
  maxYear: string;
  maxCondoFees: string;
  garageSpaces: string;
  drivewaySpaces: string;
  petFriendly: '' | 'yes';
  heatingType: string;
  basementStatus: string;
  amenities: string[];
  // A rental's lease (rentals filters, 2026-09-24).
  /** Free by this date (YYYY-MM-DD); a rental that does not say is kept. */
  availableBy: string;
  /** Any of these (furnished, semi, unfurnished). */
  furnished: string[];
  /** All of these in the rent. */
  rentIncludes: string[];
};

export const EMPTY_FILTERS: FilterValues = {
  minPrice: '',
  maxPrice: '',
  bedrooms: '',
  bathrooms: '',
  propertyType: [],
  ownerType: '',
  minArea: '',
  maxArea: '',
  minYear: '',
  maxYear: '',
  maxCondoFees: '',
  garageSpaces: '',
  drivewaySpaces: '',
  petFriendly: '',
  heatingType: '',
  basementStatus: '',
  amenities: [],
  availableBy: '',
  furnished: [],
  rentIncludes: [],
};

const SIMPLE_KEYS = [
  'minPrice', 'maxPrice', 'bedrooms', 'bathrooms', 'minArea', 'maxArea', 'minYear', 'maxYear',
  'maxCondoFees', 'garageSpaces', 'drivewaySpaces', 'heatingType', 'basementStatus', 'availableBy',
] as const;

/** List filters, written to the address as "a,b,c". */
const LIST_KEYS = ['propertyType', 'amenities', 'furnished', 'rentIncludes'] as const;

/** The "More filters" part, to open it when one of them is already set. */
export const MORE_KEYS: Array<keyof FilterValues> = [
  'minArea', 'maxArea', 'minYear', 'maxYear', 'maxCondoFees', 'garageSpaces', 'drivewaySpaces',
  'petFriendly', 'heatingType', 'basementStatus', 'amenities',
];

const list = (value: string | null) => (value || '').split(',').filter(Boolean);

/**
 * The filters in an address. A quick tag from an older link (?tag=condo) is
 * read as the filter it stands for, so the panel shows it and can undo it.
 */
export function readFilters(params: URLSearchParams): FilterValues {
  const values: FilterValues = { ...EMPTY_FILTERS, propertyType: [], amenities: [], furnished: [], rentIncludes: [] };
  for (const key of SIMPLE_KEYS) values[key] = params.get(key) || '';
  for (const key of LIST_KEYS) values[key] = list(params.get(key));
  const owner = params.get('ownerType');
  values.ownerType = owner === 'private' || owner === 'agency' ? owner : '';
  values.petFriendly = params.get('petFriendly') === 'yes' ? 'yes' : '';

  const tag = params.get('tag');
  if (tag === 'condo' && values.propertyType.length === 0) values.propertyType = [...CONDO_TYPES];
  if (tag === 'house' && values.propertyType.length === 0) values.propertyType = [...HOUSE_TYPES];
  if (tag === 'luxury' && !values.minPrice) values.minPrice = '1000000';
  if (tag === 'waterfront' && !values.amenities.includes('waterfront')) values.amenities = [...values.amenities, 'waterfront'];
  if (tag === 'low_condo_fees' && !values.maxCondoFees) values.maxCondoFees = String(LOW_CONDO_FEES);
  if (tag === 'with_garage' && !values.garageSpaces) values.garageSpaces = '1';
  return values;
}

/**
 * The address with these filters. Everything else in it - the search text,
 * a city chosen on the home page, the sort - stays; the page goes back to 1.
 * A quick tag the panel has turned into a filter is dropped; "new listings"
 * has no filter of its own and stays.
 */
export function writeFilters(params: URLSearchParams, values: FilterValues): URLSearchParams {
  const next = new URLSearchParams(params.toString());
  for (const key of SIMPLE_KEYS) {
    const value = values[key].trim();
    if (value && value !== '0') next.set(key, value);
    else next.delete(key);
  }
  for (const key of LIST_KEYS) {
    if (values[key].length) next.set(key, values[key].join(','));
    else next.delete(key);
  }
  if (values.ownerType) next.set('ownerType', values.ownerType);
  else next.delete('ownerType');
  if (values.petFriendly) next.set('petFriendly', values.petFriendly);
  else next.delete('petFriendly');
  // Tags the panel has no filter for stay; the others became filters above.
  const tag = next.get('tag');
  if (tag !== 'new_listings' && tag !== 'price_reduced') next.delete('tag');
  next.delete('page');
  return next;
}

/** How many filters are on - the number on the Filters button. */
export function activeCount(values: FilterValues): number {
  let n = 0;
  for (const key of SIMPLE_KEYS) if (values[key] && values[key] !== '0') n++;
  for (const key of LIST_KEYS) n += values[key].length;
  if (values.ownerType) n++;
  if (values.petFriendly) n++;
  return n;
}

/** The choices of each list in the panel. An empty value means "no limit". */
export const PRICE_STEPS = [
  100_000, 150_000, 200_000, 250_000, 300_000, 350_000, 400_000, 450_000, 500_000, 600_000, 700_000,
  800_000, 900_000, 1_000_000, 1_250_000, 1_500_000, 2_000_000, 2_500_000, 3_000_000, 5_000_000,
];
export const AREA_STEPS = [500, 750, 1000, 1250, 1500, 2000, 2500, 3000, 4000, 5000];
export const YEAR_STEPS = [1950, 1970, 1980, 1990, 2000, 2010, 2015, 2020, 2024];
export const CONDO_FEE_STEPS = [200, 300, 400, 500, 750, 1000];

// A month's rent (rentals, 2026-09-24): its own scale, as Centris keeps a
// second price slider for rent - $500 to $8,000 a month.
export const RENT_STEPS = [500, 750, 1000, 1250, 1500, 1750, 2000, 2250, 2500, 3000, 3500, 4000, 5000, 6000, 8000];

// "Low condo fees" as a quick tag (owner's approval, 2026-09-24): the middle
// step above, a month's fees of $400 or less. The tag used to do nothing.
export const LOW_CONDO_FEES = 400;
